/**
 * Вахтовый посёлок (РС-15): места в общежитиях и вагон-домах, заселение по факту заезда вахты, выезд — по
 * факту выезда, питание по дням, занятость против плана вахт и мест, стоимость проживания и питания.
 */
import { CAMP_KINDS, type CampView } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from '../schedule/LField';

const dd = (s: string) => s.slice(0, 10).split('-').reverse().join('.');
// Дата по часам смотрящего, а не по UTC
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function CampPanel({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['camp', projectId],
    queryFn: () => api<CampView>(`/projects/${projectId}/camp`),
  });
  const [unit, setUnit] = useState({ name: '', kind: 'trailer', places: '4' });
  const [target, setTarget] = useState<Record<string, string>>({});
  const refresh = () => qc.invalidateQueries({ queryKey: ['camp', projectId] });
  const call = useMutation({
    mutationFn: (x: { url: string; method?: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      api(x.url, { method: x.method ?? 'POST', body: x.body }),
    onSuccess: () => void refresh(),
    onError: (e) => toast(e.message, 'danger'),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  const max = Math.max(1, ...v.days.map((d) => Math.max(d.living, d.places, d.planned)));
  const cost = v.days.some((d) => d.cost != null) ? v.days.reduce((n, d) => n + (d.cost ?? 0), 0) : null;
  const t = today();
  return (
    <div className="flex flex-col gap-4">
      <Panel title="Места проживания" meta={`${v.units.reduce((n, u) => n + u.places, 0)} мест`}>
        {!v.units.length ? (
          <EmptyState title="Мест пока нет">
            Добавьте общежития, вагон-дома или гостиницы проекта с числом мест.
          </EmptyState>
        ) : (
          <ul className="divide-y divide-line text-[14px]">
            {v.units.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <span>
                  {u.name} <span className="text-ink-2">· {CAMP_KINDS[u.kind]}</span>
                </span>
                <Badge
                  tone={u.occupied > u.places ? 'danger' : u.occupied === u.places ? 'warning' : undefined}
                >
                  занято {u.occupied} из {u.places}
                </Badge>
              </li>
            ))}
          </ul>
        )}
        {canEdit ? (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <LField label="Название">
              <Input
                value={unit.name}
                onChange={(e) => setUnit({ ...unit, name: e.target.value })}
                className="w-56 max-w-full max-md:h-11"
              />
            </LField>
            <LField label="Вид">
              <Select
                value={unit.kind}
                onChange={(e) => setUnit({ ...unit, kind: e.target.value })}
                className="max-md:h-11"
              >
                {Object.entries(CAMP_KINDS).map(([k, title]) => (
                  <option key={k} value={k}>
                    {title}
                  </option>
                ))}
              </Select>
            </LField>
            <LField label="Мест">
              <Input
                inputMode="numeric"
                value={unit.places}
                onChange={(e) => setUnit({ ...unit, places: e.target.value })}
                className="w-20 max-md:h-11"
              />
            </LField>
            <Button
              disabled={!unit.name.trim() || !(Number(unit.places) >= 1) || call.isPending}
              onClick={() => {
                call.mutate({
                  url: `/projects/${projectId}/camp-units`,
                  body: { name: unit.name, kind: unit.kind, places: Math.round(Number(unit.places)) },
                });
                setUnit({ ...unit, name: '' });
              }}
              className="max-md:h-11"
            >
              Добавить место проживания
            </Button>
          </div>
        ) : null}
      </Panel>
      {v.unhoused.length ? (
        <Panel title="Заехали, но не заселены" meta={`${v.unhoused.length}`}>
          <ul className="divide-y divide-line text-[14px]">
            {v.unhoused.map((x) => (
              <li key={x.rotationId} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <span>
                  {x.workerName} <span className="text-ink-2">· заезд {dd(x.actualStart)}</span>
                </span>
                {canEdit && v.units.length ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <Select
                      value={target[x.rotationId] ?? v.units[0]!.id}
                      onChange={(e) => setTarget({ ...target, [x.rotationId]: e.target.value })}
                      aria-label="Куда заселить"
                      className="max-md:h-11"
                    >
                      {v.units.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.places - u.occupied} свободно)
                        </option>
                      ))}
                    </Select>
                    <Button
                      size="sm"
                      disabled={call.isPending}
                      onClick={() =>
                        call.mutate({
                          url: `/camp-units/${target[x.rotationId] ?? v.units[0]!.id}/stays`,
                          body: { rotationId: x.rotationId },
                        })
                      }
                      className="max-md:h-11"
                    >
                      Заселить
                    </Button>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
      <Panel title="Занятость по дням" meta={`${dd(v.from)} — ${dd(v.to)}`}>
        <div
          className="flex items-end gap-0.5 overflow-x-auto pb-1"
          role="img"
          aria-label="Живут, мест и по плану вахт по дням"
        >
          {v.days.map((d) => (
            <div key={d.day} className="flex w-3 shrink-0 flex-col items-center">
              <div className="relative h-28 w-3">
                <div
                  className={clsx(
                    'absolute bottom-0 w-3 rounded-t',
                    d.living > d.places ? 'bg-danger' : 'bg-ink-2',
                  )}
                  style={{ height: `${(d.living / max) * 100}%` }}
                  title={`${dd(d.day)}: живут ${d.living}, мест ${d.places}, по плану ${d.planned}`}
                />
                <div
                  className="absolute w-3 border-t-2 border-ink"
                  style={{ bottom: `${(d.places / max) * 100}%` }}
                />
                <div
                  className="absolute w-3 border-t-2 border-dashed border-danger"
                  style={{ bottom: `${(d.planned / max) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[12px] text-ink-2">
          Столбец — живут (питание — столько же порций), сплошная черта — мест, пунктир — по плану вахт на
          площадке.
          {cost != null ? ` Проживание и питание за период: ${tenge(cost)}.` : ''}
        </p>
        {v.overbooked.length ? (
          <p className="mt-1 text-[13px] text-danger">
            Мест не хватает:{' '}
            {v.overbooked
              .slice(0, 3)
              .map((o) => `${o.name} ${dd(o.day)} (${o.living} на ${o.places})`)
              .join(', ')}
          </p>
        ) : null}
      </Panel>
      <Panel title="Проживают">
        {v.stays.length ? (
          <ul className="divide-y divide-line text-[14px]">
            {v.stays.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <span>
                  {s.workerName}
                  <span className="block text-[12px] text-ink-2">
                    {s.unitName} · с {dd(s.checkIn)}
                    {s.checkOut ? ` по ${dd(s.checkOut)}` : ''}
                  </span>
                </span>
                {canEdit && !s.checkOut && s.checkIn <= t ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={call.isPending}
                    onClick={() =>
                      call.mutate({ url: `/camp-stays/${s.id}`, method: 'PATCH', body: { checkOut: t } })
                    }
                    className="max-md:h-11"
                  >
                    Выселить сегодня
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[14px] text-ink-2">Никто не проживает.</p>
        )}
      </Panel>
    </div>
  );
}
